import React, { useState } from 'react';
import "../../Css/Devices.css";
import StatusBadge from '../../Components/shared/status.jsx';
import PageLayout from '../../Components/PageLayout';
import ImageWithLoader from '../../Components/common/ImageWithLoader';
import { useUserPrinters } from '../../hooks/usePrinters';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function DevicesScreen() {
    const [searchTerm, setSearchTerm] = useState('');
    const { data: printers, isLoading, isError, error } = useUserPrinters();

    const getFullIconUrl = (iconUrl) => {
        if (!iconUrl) return null;
        // If it's already a full URL (Google Cloud Storage), use it directly
        if (iconUrl.startsWith('http') || iconUrl.startsWith('//')) return iconUrl;
        // If it's a relative path, prepend API URL for backward compatibility
        return `${API_URL}${iconUrl}`;
    };

    // Debug log
    console.log('Printers data:', printers);
    console.log('Is loading:', isLoading);
    console.log('Is error:', isError);
    console.log('Error:', error);

    // Filter printers based on search term
    const filteredPrinters = printers.filter(printer =>
        printer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (printer.modelDisplayName || printer.model).toLowerCase().includes(searchTerm.toLowerCase()) ||
        printer.serialNumber.toLowerCase().includes(searchTerm.toLowerCase())
    );

    console.log('Filtered printers:', filteredPrinters);

    if (isLoading) {
        return (
            <PageLayout title="Printers" subtitle="Devices overview">
                <div className="loading-message">Loading printers...</div>
            </PageLayout>
        );
    }

    if (isError) {
        return (
            <PageLayout title="Printers" subtitle="Devices overview">
                <div className="error-message">
                    Error loading printers: {error?.message}
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout title="Printers" subtitle="Devices overview">
            <div className="flex title-box">
                <p className="small-text">Search</p>
                <input 
                    className="devices-search top-border" 
                    type="text" 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, model, or serial number..."
                />
                <button className="filter-btn top-border">Filter</button>
            </div>
            
            <div className="devices-list">
                <table className="devices-table">
                    <thead>
                        <tr className='table-header'>
                            <th className='small-text table-thumb'>Icon</th>
                            <th className='table-title'>Title</th>
                            <th className='table-devices table-model'>Model</th>
                            <th className='table-devices'>Status</th>
                            <th className='table-devices'>Hub</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPrinters.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="no-data-message">
                                    {searchTerm ? 'No printers found matching your search.' : 'No printers configured yet.'}
                                </td>
                            </tr>
                        ) : (
                            filteredPrinters.map((printer) => {
                                console.log(`Printer: ${printer.name}, icon_url: "${printer.icon_url}"`);
                                const hasIcon = printer.icon_url && printer.icon_url.trim() !== '';
                                console.log(`Has icon: ${hasIcon}`);
                                return (
                                <tr className="table-devices-tr" key={printer.uuid}>
                                    <td>
                                        <div className="printer-icon-container">
                                            {hasIcon ? (
                                                <ImageWithLoader
                                                    src={getFullIconUrl(printer.icon_url)}
                                                    alt={`${printer.name} icon`}
                                                    className="printer-icon"
                                                    fallbackText={printer.name.charAt(0).toUpperCase()}
                                                    iconType="printer"
                                                    size="preview"
                                                    onError={() => {
                                                        console.log(`Icon failed to load for ${printer.name}: ${getFullIconUrl(printer.icon_url)}`);
                                                    }}
                                                    onLoad={() => {
                                                        console.log(`Icon loaded: ${printer.name}`);
                                                    }}
                                                />
                                            ) : (
                                                <div className="printer-icon-placeholder">
                                                    {printer.name.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="printer-name-container">
                                            <div className="printer-name">{printer.name}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="td-rounded">
                                            {printer.modelDisplayName || printer.model}
                                        </div>
                                    </td>
                                    <td><StatusBadge status={printer.statusValue}/></td>
                                    <td>
                                        {printer.hub ? (
                                            <div className="hub-thumb">
                                                {printer.hub.avatar_base64 ? (
                                                    <img
                                                        src={`data:image/png;base64,${printer.hub.avatar_base64}`}
                                                        alt={`${printer.hub.name} avatar`}
                                                        className="hub-avatar-pixelated"
                                                        title={printer.hub.name}
                                                    />
                                                ) : (
                                                    <div className="avatar-placeholder" title={printer.hub.name}>
                                                        {printer.hub.name ? printer.hub.name.charAt(0) : "H"}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="no-hub-thumb" title="No Hub Connected">
                                                <div className="avatar-placeholder">
                                                    ❌
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </PageLayout>
    );
}

export default DevicesScreen;
